import type {DataLayout, GridSection} from 'analysis-tool/data-layout/DataLayoutTypes';

import {PLACEHOLDER_BLOCK} from './LayoutConstants';

export interface LayoutDimensions {
  rows: number;
  columns: number;
}

export interface ResizeResult {
  config: {name: DataLayout['name']; sections: DataLayout['sections']};
  displacedBlockCount: number;
}

/**
 * Reads the dimensions (rows and columns) from the first GRID section of a layout config.
 */
export function getLayoutDimensions(sections: DataLayout['sections']): LayoutDimensions {
  const gridSection = sections.find((section): section is GridSection => section.type === 'GRID');
  if (!gridSection || gridSection.rows.length === 0) {
    return {rows: 0, columns: 0};
  }
  return {
    rows: gridSection.rows.length,
    columns: gridSection.rows[0].length,
  };
}

/**
 * Resizes the layout grid to the specified dimensions, preserving existing blocks.
 *
 * Growing appends PLACEHOLDER blocks to existing rows or appends new rows of PLACEHOLDER blocks.
 * Shrinking truncates rows/columns and counts displaced non-placeholder blocks.
 */
export function resizeLayoutGrid(
  sections: DataLayout['sections'],
  newName: DataLayout['name'],
  newRows: number,
  newColumns: number
): ResizeResult {
  const gridSectionIndex = sections.findIndex((section): section is GridSection => section.type === 'GRID');
  if (gridSectionIndex === -1) {
    return {config: {name: newName, sections}, displacedBlockCount: 0};
  }

  const gridSection = sections[gridSectionIndex] as GridSection;
  let displacedBlockCount = 0;

  const resizedRows = Array.from({length: newRows}, (_, rowIndex) => {
    if (rowIndex < gridSection.rows.length) {
      const existingRow = gridSection.rows[rowIndex];
      if (newColumns <= existingRow.length) {
        for (let colIndex = newColumns; colIndex < existingRow.length; colIndex++) {
          if (existingRow[colIndex].type !== 'PLACEHOLDER') {
            displacedBlockCount++;
          }
        }
        return existingRow.slice(0, newColumns);
      } else {
        return [
          ...existingRow,
          ...Array.from({length: newColumns - existingRow.length}, () => PLACEHOLDER_BLOCK),
        ];
      }
    } else {
      return Array.from({length: newColumns}, () => PLACEHOLDER_BLOCK);
    }
  });

  for (let rowIndex = newRows; rowIndex < gridSection.rows.length; rowIndex++) {
    for (const block of gridSection.rows[rowIndex]) {
      if (block.type !== 'PLACEHOLDER') {
        displacedBlockCount++;
      }
    }
  }

  const newSections = [...sections];
  newSections[gridSectionIndex] = {
    ...gridSection,
    rows: resizedRows,
  };

  return {
    config: {name: newName, sections: newSections},
    displacedBlockCount,
  };
}
